// response-intercept.js -- reusable capture-stage reader for SlipSync books (ADR 006, #885).
// Single job: build the documentStart script that clones the book page's OWN responses
// matching a capture spec and posts each one to the app as a non-terminal capture_page
// message. It never issues a request, never reads cookies/storage, and never records a
// request header value except cfg.keptHeader (posted in its stored form). XHR is the primary
// path (FanDuel loads My Bets via axios/XHR -- docs/research/2026-10-08-fanduel-mybets-payload.md
// row 8); fetch is wrapped for other books.
//
// Page metadata is posted already in the server's stored form (plan Post-PR-1 P1/P2), so the
// server pass (api/lib/slipsync/capture-validator.js) is a fixed point and a malformed value
// never costs a page. The contracts below mirror that file; slipsync-capture-parity.test.js
// pins them behaviourally (app code must not import api/ -- CRA ModuleScopePlugin).
//
// captureInjectedMain is self-contained ES5 (no closures over module scope, no arrows)
// so Function.prototype.toString() serialization is faithful. `w` is injectable for
// tests; the shipped script calls it with cfg only and it uses `window`.

// Reviewed media types and charset values (capture-validator.js storedContentType).
export const CAPTURE_MEDIA_TYPES = ['application/json', 'text/json', 'application/problem+json', 'application/javascript',
  'text/javascript', 'text/plain', 'text/html', 'application/octet-stream', 'application/x-www-form-urlencoded'];
export const CAPTURE_CHARSETS = ['utf-8', 'utf8', 'iso-8859-1', 'us-ascii'];
// Query key-label rule (capture-policy.js keyLabelOk, minus its redactTokens step, which the
// server adds -- it can only drop more). Sources plus flags so nothing is lost in JSON.
export const CAPTURE_KEY_LABEL = {
  label: '^[A-Za-z_][A-Za-z0-9_]{0,39}$',
  vendor: '^(?:AKIA|ASIA|ABIA|ACCA|AIza|sk_|rk_|pk_|ghp_|gho_|ghu_|ghs_|ghr_|github_pat_|xox)',
  email: '(?:^|_)(?:gmail|yahoo|hotmail|outlook|icloud|aol|proton|protonmail|com|net|org|edu)(?:_|$)',
  emailFlags: 'i',
};

export function captureInjectedMain(cfg, w) {
  try {
    w = w || window;
    if (w.top !== w) return;
    if (!w.mobileApp || typeof w.mobileApp.postMessage !== 'function') return;
    // Only the book's own documents install (pre-Codex Sec-1): the in-app browser can reach
    // other sites, and the documentStart script runs on every navigation.
    var sfx = typeof cfg.pageHostSuffix === 'string' ? cfg.pageHostSuffix.toLowerCase() : '';
    var pageHost = String((w.location && w.location.host) || '').toLowerCase();
    if (!sfx || !(pageHost === sfx || pageHost.slice(-(sfx.length + 1)) === '.' + sfx)) return;
    // Per-open nonce: the bridge drops any message without it, so a subframe or another
    // document that posts to the native handler directly cannot inject pages.
    if (typeof cfg.nonce !== 'string' || !cfg.nonce) return;
    if (w.__stCaptureInstalled) return;
    w.__stCaptureInstalled = true;

    var U = w.URL || URL;
    var MAX_URL = 300;
    var pathRe = new RegExp(cfg.pathPattern);
    var keptHeader = String(cfg.keptHeader).toLowerCase();
    var NAME_RE = /^[a-z0-9-]{1,50}$/;
    var QUERY_KEY = /^[A-Za-z0-9_.-]{1,40}$/;
    var labelRe = new RegExp(cfg.keyLabel.label);
    var vendorRe = new RegExp(cfg.keyLabel.vendor);
    var emailRe = new RegExp(cfg.keyLabel.email, cfg.keyLabel.emailFlags);
    var CHARSET_RE = /;\s*charset\s*=\s*"?([A-Za-z0-9._-]{1,20})"?\s*(?:;|$)/i;

    var match = function (raw) {
      try {
        var x = new U(String(raw), w.location.href);
        if (x.protocol !== 'https:') return null;
        if (cfg.hosts.indexOf(x.host) === -1) return null;
        return pathRe.test(x.pathname) ? x : null;
      } catch (e) { return null; }
    };
    var isKept = function (k) { return Object.prototype.hasOwnProperty.call(cfg.keptQuery, k); };
    var labelOk = function (k) {
      return labelRe.test(k) && (k.match(/\d/g) || []).length < 3 && !vendorRe.test(k) && !emailRe.test(k);
    };
    // Same algorithm as redactCaptureUrl: drop a pair whose key is neither kept nor a clean
    // label; keep a kept value only on a full grammar match (decoded); drop whole trailing
    // pairs until the URL fits. Returns null when the base alone is too long.
    var cleanUrl = function (x) {
      var base = x.origin + x.pathname;
      if (base.length > MAX_URL) return null;
      var parts = [];
      x.searchParams.forEach(function (v, k) {
        if (!QUERY_KEY.test(k)) return;
        var kept = isKept(k);
        if (!kept && !labelOk(k)) return;
        parts.push(encodeURIComponent(k) + '=' + (kept && new RegExp(cfg.keptQuery[k]).test(v) ? encodeURIComponent(v) : ':redacted'));
      });
      while (parts.length && (base + '?' + parts.join('&')).length > MAX_URL) parts.pop();
      return parts.length ? base + '?' + parts.join('&') : base;
    };
    var cleanContentType = function (ct) {
      if (ct === null || ct === undefined || ct === '') return '';
      var s = String(ct);
      var type = s.split(';')[0].trim().toLowerCase();
      if (cfg.mediaTypes.indexOf(type) === -1) return ':redacted';
      var m = CHARSET_RE.exec(s);
      var cs = m && m[1].toLowerCase();
      return cs && cfg.charsets.indexOf(cs) !== -1 ? type + '; charset=' + cs : type;
    };
    var noteHeader = function (rec, name, value) {
      var n = String(name).toLowerCase();
      if (NAME_RE.test(n) && rec.names.indexOf(n) === -1 && rec.names.length < 30) rec.names.push(n);
      if (n === keptHeader) rec.region = (typeof value === 'string' && /^[A-Za-z]{2}$/.test(value)) ? value.toUpperCase() : ':redacted';
    };
    var post = function (rec, status, ct, text) {
      try {
        // Spec §6: method is GET|POST; anything else fails closed (never relabelled).
        if (rec.m !== 'GET' && rec.m !== 'POST') return;
        var url = cleanUrl(rec.x);
        if (!url) return;
        var t = typeof text === 'string' ? text : '';
        w.mobileApp.postMessage({ detail: { type: 'capture_page', nonce: cfg.nonce, page: {
          url: url,
          status: status,
          contentType: cleanContentType(ct),
          body: t.slice(0, cfg.maxBodyChars),
          truncated: t.length > cfg.maxBodyChars,
          method: rec.m,
          regionHeader: rec.region,
          ts: Date.now(),
          headerNames: rec.names.slice(0, 30)
        } } });
      } catch (e) { /* the page must never see our failure */ }
    };

    var XHR = w.XMLHttpRequest;
    if (XHR && XHR.prototype) {
      var xo = XHR.prototype.open, xs = XHR.prototype.setRequestHeader, xsend = XHR.prototype.send;
      XHR.prototype.open = function (method, url) {
        try { this.__stRec = { x: match(url), m: String(method || 'GET').toUpperCase(), names: [], region: null }; } catch (e) { this.__stRec = null; }
        return xo.apply(this, arguments);
      };
      XHR.prototype.setRequestHeader = function (name, value) {
        try { var r = this.__stRec; if (r && r.x) noteHeader(r, name, value); } catch (e) { /* ignore: header bookkeeping only */ }
        return xs.apply(this, arguments);
      };
      XHR.prototype.send = function () {
        try {
          var r = this.__stRec, xhr = this;
          if (r && r.x) {
            xhr.addEventListener('load', function () {
              // A reused XHR keeps this listener after a later open(): only its own request posts (A7).
              if (xhr.__stRec !== r) return;
              try {
                var rt = xhr.responseType, text = null;
                if (rt === '' || rt === 'text') text = xhr.responseText;
                else if (rt === 'json') text = JSON.stringify(xhr.response);
                if (typeof text === 'string') post(r, xhr.status, xhr.getResponseHeader('content-type'), text);
              } catch (e) { /* ignore: never break the page's own load handlers */ }
            });
          }
        } catch (e) { /* ignore: the page's send must still run */ }
        return xsend.apply(this, arguments);
      };
    }

    var F = w.fetch;
    if (typeof F === 'function') {
      w.fetch = function (input, init) {
        var p = F.apply(this, arguments);
        try {
          // A string, a Request (.url) or a URL object (.href).
          var url = typeof input === 'string' ? input
            : input && typeof input.url === 'string' ? input.url
            : input && typeof input.href === 'string' ? input.href : '';
          var x = match(url);
          if (x) {
            var rec = { x: x, m: String((init && init.method) || (input && input.method) || 'GET').toUpperCase(), names: [], region: null };
            var h = (init && init.headers) || (input && input.headers);
            if (h) {
              if (typeof h.forEach === 'function' && !Array.isArray(h)) h.forEach(function (v, k) { noteHeader(rec, k, v); });
              else if (Array.isArray(h)) { for (var i = 0; i < h.length; i++) if (h[i]) noteHeader(rec, h[i][0], h[i][1]); }
              else { for (var k in h) if (Object.prototype.hasOwnProperty.call(h, k)) noteHeader(rec, k, h[k]); }
            }
            p.then(function (res) {
              try {
                res.clone().text().then(function (t) { post(rec, res.status, res.headers.get('content-type'), t); }, function () {});
              } catch (e) { /* ignore: an unclonable response is skipped */ }
            }, function () {});
          }
        } catch (e) { /* ignore: the page still gets its own promise */ }
        return p;
      };
    }

    // Install probe (A8): the bridge logs the typeof of each field so the simulator smoke
    // (A13) proves the message channel keeps types.
    try { w.mobileApp.postMessage({ detail: { type: 'capture_ready', nonce: cfg.nonce, probe: { b: true, n: null, i: 1, s: 'ok' } } }); } catch (e) { /* ignore: diagnostics only */ }
  } catch (e) { /* never break the page */ }
}

// The cfg the injected script receives: the book's capture spec plus the shared
// stored-metadata contracts (G4) and the per-open nonce. The spec object stays book-only.
export function captureCfg(spec, nonce) {
  return Object.assign({}, spec, { mediaTypes: CAPTURE_MEDIA_TYPES, charsets: CAPTURE_CHARSETS, keyLabel: CAPTURE_KEY_LABEL, nonce: nonce });
}

export function buildCaptureScript(spec, nonce) {
  return '(' + captureInjectedMain.toString() + ')(' + JSON.stringify(captureCfg(spec, nonce)) + ');';
}
